export interface DailyEntryWarning {
  code: string;
  message: string;
  severity: "warning" | "error";
}

export interface DailyEntryWarningRow {
  hours: number;
  jobCode?: string | null;
  equipment: {
    equipmentId: string;
    usageHours: number;
    equipmentHoursOverrideReason?: string | null;
  }[];
}

export interface RowWarningEquipmentMeta {
  id: string;
  name: string;
  equipmentId: string;
  status: string;
  siteId: string | null;
  requiredCertification: string | null;
}

export interface RowWarningWorkerCert {
  type: string;
  status: string;
}

export interface RowWarningContext {
  siteId: string;
  equipmentById: Record<string, RowWarningEquipmentMeta>;
  workerCertifications?: RowWarningWorkerCert[];
}

function buildCertificationWarnings(
  equipmentMeta: RowWarningEquipmentMeta,
  workerName: string,
  workerCertifications: RowWarningWorkerCert[],
): DailyEntryWarning[] {
  const required = equipmentMeta.requiredCertification;
  if (!required) return [];

  const match = workerCertifications.find((cert) => cert.type === required);
  if (!match) {
    return [
      {
        code: "UNCERTIFIED_OPERATOR",
        message: `${workerName}: missing certification for ${equipmentMeta.name}`,
        severity: "warning",
      },
    ];
  }

  if (match.status === "EXPIRED") {
    return [
      {
        code: "UNCERTIFIED_OPERATOR",
        message: `${workerName}: expired certification for ${equipmentMeta.name}`,
        severity: "warning",
      },
    ];
  }

  if (match.status === "PENDING") {
    return [
      {
        code: "UNCERTIFIED_OPERATOR",
        message: `${workerName}: certification pending for ${equipmentMeta.name}`,
        severity: "warning",
      },
    ];
  }

  return [];
}

/** Client/server shared validation for daily entry row exceptions. */
export function buildRowWarnings(
  row: DailyEntryWarningRow,
  workerName: string,
  context?: RowWarningContext,
): DailyEntryWarning[] {
  const warnings: DailyEntryWarning[] = [];

  if (row.hours < 0) {
    warnings.push({
      code: "NEGATIVE_HOURS",
      message: "Hours cannot be negative",
      severity: "error",
    });
  }

  if (row.hours > 16) {
    warnings.push({
      code: "LONG_DAY",
      message: `${workerName}: ${row.hours}h exceeds 16h`,
      severity: "warning",
    });
  }

  if (!row.jobCode?.trim()) {
    warnings.push({
      code: "MISSING_JOB_CODE",
      message: `Missing job code for ${workerName}`,
      severity: "warning",
    });
  }

  for (const item of row.equipment) {
    if (!item.equipmentId) continue;

    const meta = context?.equipmentById[item.equipmentId];

    if (item.equipmentId && item.usageHours <= 0) {
      warnings.push({
        code: "EQUIPMENT_NO_HOURS",
        message: `Equipment without hours for ${workerName}`,
        severity: "warning",
      });
    }

    if (item.usageHours > row.hours && !item.equipmentHoursOverrideReason?.trim()) {
      warnings.push({
        code: "EQUIPMENT_HOURS_EXCEED",
        message: `Equipment hours exceed worker hours for ${workerName}`,
        severity: "error",
      });
    }

    if (context && meta) {
      if (meta.status !== "AVAILABLE") {
        warnings.push({
          code: "UNAVAILABLE_EQUIPMENT",
          message: `${meta.name} is ${meta.status.replaceAll("_", " ").toLowerCase()} for ${workerName}`,
          severity: "warning",
        });
      }

      if (meta.siteId && meta.siteId !== context.siteId) {
        warnings.push({
          code: "EQUIPMENT_WRONG_SITE",
          message: `${meta.name} is assigned to a different site for ${workerName}`,
          severity: "warning",
        });
      }

      if (context.workerCertifications) {
        warnings.push(
          ...buildCertificationWarnings(meta, workerName, context.workerCertifications),
        );
      }
    } else if (context && item.equipmentId) {
      warnings.push({
        code: "EQUIPMENT_WRONG_SITE",
        message: `Unknown equipment on row for ${workerName}`,
        severity: "warning",
      });
    }
  }

  return warnings;
}

export function countRowExceptions(warnings: DailyEntryWarning[]): number {
  return warnings.filter((warning) => warning.severity === "error").length;
}

export function buildEquipmentByIdMap(
  equipment: RowWarningEquipmentMeta[],
): Record<string, RowWarningEquipmentMeta> {
  return Object.fromEntries(equipment.map((item) => [item.id, item]));
}
